// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Main component for the asset metadata upload feature
 */
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import {
  AssetMetadataUploadResult,
  uploadAssetMetadata,
  uploadAssetMetadataResponseSchema,
} from '@/asset/api/upload-asset-metadata';
import { CsvUpload } from '@/asset/components/asset-metadata-upload/csv-upload';
import { ItemSelection } from '@/asset/components/asset-metadata-upload/item-selection';
import { Results } from '@/asset/components/asset-metadata-upload/results';
import { Stepper } from '@/asset/components/asset-metadata-upload/stepper';
import { TemplateDownload } from '@/asset/components/asset-metadata-upload/template-download';
import type {
  ExpandedAssetsState,
  Step,
} from '@/asset/components/asset-metadata-upload/types';
import { useSupportError } from '@/hooks/use-support-error';
import { getItem } from '@/items/api/get-item';
import { getItems } from '@/items/api/get-items';
import { Item, ItemDetail } from '@/items/api/types';
import { Box } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useRef, useState } from 'react';

interface Props {
  onClose: () => void;
}

/**
 * Component for uploading asset metadata from CSV files.
 * Provides a multi-step interface for selecting a product type,
 * downloading a CSV template, uploading the filled template, and viewing results.
 */
export function AssetMetadataUpload(props: Props) {
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('select-product');
  const [selectedItem, setSelectedItem] = useState<ItemDetail | undefined>(
    undefined
  );
  const [selectedItemKey, setSelectedItemKey] = useState<string | undefined>(
    undefined
  );
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] =
    useState<AssetMetadataUploadResult | null>(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedAssets, setExpandedAssets] = useState<ExpandedAssetsState>({
    updated: false,
    notUpdated: false,
    failed: false,

    structureErrors: false,
  });
  const [hasFatalError, setHasFatalError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Fetch list of items (without fields)
  const { data: items, isLoading: isLoadingItems } = useQuery<Item[]>({
    queryKey: ['item', 'DEVICE'],
    queryFn: async () => {
      const response = await getItems({ type: 'DEVICE', limit: 1000 });
      return response.results;
    },
  });

  // Fetch item details (with fields) when an item is selected
  const { data: itemDetail, isLoading: isLoadingItemDetail } = useQuery({
    queryKey: ['item', selectedItemKey],
    queryFn: async () => {
      if (!selectedItemKey) return null;
      const response = await getItem(selectedItemKey);
      return response.item;
    },
    enabled: Boolean(selectedItemKey),
  });

  // Update selectedItem when itemDetail loads
  if (itemDetail && selectedItem?.id !== itemDetail.id) {
    setSelectedItem(itemDetail);
  }

  const { mutate: uploadMetadata, isPending: isUploading } = useMutation({
    mutationFn: async () => {
      if (!file || !selectedItem) {
        throw new Error('Missing file or item selection');
      }

      const formData = new FormData();
      formData.append('metadata', file);
      formData.append('productId', selectedItem.id); // Backend still uses productId

      return uploadAssetMetadata(formData);
    },
    onSuccess: (data) => {
      clearFile();
      setUploadResult(data);
      setUploadErrorMessage(null);
      setCurrentStep('results');
      queryClient.invalidateQueries({ queryKey: ['asset'] });
    },
    onError: (error: AxiosError) => {
      clearFile();

      // First try to parse as a response with validation errors
      const knownError = uploadAssetMetadataResponseSchema.safeParse(
        error.response?.data
      );
      if (knownError.success) {
        // This is a known error that comes back due to the fact it rolls back the transaction if any of the rows are bad.
        // A transaction rollback always results in a 400 or 500-level status code
        // The knownError fails to parse when the data body is missing which happens on unexpected errors or initial validation errors
        setUploadResult(knownError.data.data);
        setUploadErrorMessage(null);
        setCurrentStep('results');
        return;
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(error);

      // Curated codes from ERROR_MESSAGES are the handled set: show
      // inline, skip Sentry. Anything that falls through to the support
      // fallback is unhandled and must fire Sentry.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while updating asset metadata.');
    },
  });

  /**
   * Handles item selection and fetches item details
   */
  const handleItemSelect = (item: Item | undefined) => {
    setSelectedItemKey(item?.key);
    if (!item) {
      setSelectedItem(undefined);
    }
  };

  /**
   * Handles file selection for upload
   */
  const countFileLines = async (file: File): Promise<number> => {
    const text = await file.text();
    return text.split('\n').length - 1; // -1 to exclude header row
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);
      const count = await countFileLines(selectedFile);
      setUploadLineCount(count);
    }
  };

  /**
   * Triggers the file input click
   */
  const handleSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handles the upload process
   */
  const handleUpload = () => {
    if (file) {
      uploadMetadata();
    }
  };

  /**
   * Resets the form to start a new upload
   */
  const handleStartNew = () => {
    setCurrentStep('select-product');
    setSelectedItem(undefined);
    setSelectedItemKey(undefined);
    clearFile();
    setUploadResult(null);
    setExpandedAssets({
      updated: false,
      notUpdated: false,
      failed: false,
      structureErrors: false,
    });
  };

  /**
   * Toggles the expanded state of asset lists
   */
  const toggleExpanded = (section: keyof ExpandedAssetsState) => {
    setExpandedAssets((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stepper currentStep={currentStep} />

      {(currentStep === 'select-product' || !selectedItem) && (
        <ItemSelection
          onClose={props.onClose}
          selectedItem={selectedItem}
          selectedItemKey={selectedItemKey}
          isLoadingItems={isLoadingItems}
          isLoadingItemDetail={isLoadingItemDetail}
          items={items ?? []}
          onItemSelect={handleItemSelect}
          onNext={() => setCurrentStep('download-template')}
        />
      )}

      {currentStep === 'download-template' && selectedItem && (
        <TemplateDownload
          selectedItem={selectedItem}
          onBack={() => setCurrentStep('select-product')}
          onNext={() => {
            clearFile();
            setCurrentStep('upload-csv');
          }}
        />
      )}

      {currentStep === 'upload-csv' && (
        <CsvUpload
          uploadErrorMessage={uploadErrorMessage}
          file={file}
          isUploading={isUploading}
          onSelectFile={handleSelectFile}
          onUpload={handleUpload}
          onBack={() => {
            setCurrentStep('download-template');
            setHasFatalError(false);
          }}
          onClose={props.onClose}
          hasFatalError={hasFatalError}
          uploadLineCount={uploadLineCount}
        />
      )}

      {currentStep === 'results' && (
        <Results
          selectedItem={selectedItem as ItemDetail}
          uploadResult={uploadResult}
          expandedAssets={expandedAssets}
          onToggleExpanded={toggleExpanded}
          onBack={() => setCurrentStep('upload-csv')}
          onClose={props.onClose}
          onStartNew={handleStartNew}
        />
      )}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileChange}
      />
    </Box>
  );
}
